import { BsPencil, BsPlusCircle } from 'react-icons/bs';
import { CleaningSettings } from './subcomponents/CleaningSettings';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { FailedToGetData, FloatingSaveBar, H2, Loading, RadioSelector } from 'src/components/_commons'
import { helpers, meetingServices } from 'src/services';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { millisecondsOneDay, newAttendance, newAvAssignments, newUsherAssignments } from '@monorepo/helpers';
import { RoomCleaningTable } from './subcomponents/RoomCleaningTable'
import { typeMeeting, typeMeetingsDoc } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useGetConfig } from 'src/custom-hooks';

export const AdminsRoomCleaning = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [meetingsDoc, setMeetingsDoc] = useState<typeMeetingsDoc | null>(null);
    const [meetingsToSave, setMeetingsToSave] = useState<typeMeeting[] | null>(null);
    const [radioValue, setRadioValue] = useState('1');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [showCrud, setShowCrud] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const config = useGetConfig();
    const dispatch = useDispatch();

    const sample: typeMeeting[] = useMemo(() =>
        !meetingsDoc ? [] : [...meetingsDoc.midweeks, ...meetingsDoc.publics]
            ?.filter(m =>
                radioValue === '2' || new Date(m.date).getTime() > new Date().getTime() - millisecondsOneDay
            ).sort((m1, m2) =>
                new Date(m2.date).getTime() - new Date(m1.date).getTime()
            )
        ?? []
    , [meetingsDoc, radioValue]);

    const mts: typeMeeting[] = useMemo(() =>
        meetingsToSave?.filter(m =>
            meetingsDoc ? ![...meetingsDoc.midweeks, ...meetingsDoc.publics].map(_m => _m.id).includes(m.id)  // new meeting
            || m.groupsForCleaning.some(a => a !== [...meetingsDoc.midweeks, ...meetingsDoc.publics].find(_m => _m.id === m.id)!.groupsForCleaning.find(_a => _a === a))  // cleaning changes
            || m.date !== [...meetingsDoc.midweeks, ...meetingsDoc.publics].find(_m => _m.id === m.id)!.date  // date changed
            :
            []
        ) || []
    , [meetingsDoc, meetingsToSave]);

    const meetingsToDelete: typeMeeting[] = useMemo(() =>
        meetingsDoc ?
            [...meetingsDoc.midweeks, ...meetingsDoc.publics].filter(m => !(meetingsToSave ?? []).map(_m => _m.id).includes(m.id))
            :
            []
    , [meetingsDoc, meetingsToSave]);

    const isValid: boolean = useMemo(() =>
        !!meetingsToSave
        && meetingsToSave.every(m => m.date)
        && (!!mts.length || !!meetingsToDelete.length)
    , [meetingsToDelete, meetingsToSave, mts]);

    const cancelHandler = () => {
        setShowCrud(false);
        setMeetingsToSave([...meetingsDoc!.midweeks, ...meetingsDoc!.publics].sort((m1, m2) => new Date(m2.date).getTime() - new Date(m1.date).getTime()));
        helpers.GoToTop();
    }

    const saveHandler = () => {
        if (!isValid) {
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: '',
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await meetingServices.SaveCleaningAssignments(mts, meetingsToDelete);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                setRefreshCounter(x => x + 1);
                setShowCrud(false);
                helpers.GoToTop();
            }
        }));
    }

    useEffect(() => {
        meetingServices.Get().then(response => {
            setIsLoading(false);
            if (!response.success || !response.data?.meetingsDoc) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setMeetingsDoc(response.data.meetingsDoc);
            const msts = [...response.data.meetingsDoc.publics, ...response.data.meetingsDoc.midweeks]
                // .filter(m => new Date(m.date).getTime() > new Date().getTime() - millisecondsOneDay)
                .sort((m1, m2) => new Date(m2.date).getTime() - new Date(m1.date).getTime())
            ;
            setMeetingsToSave(msts);
        });
    }, [refreshCounter]);

    if (isLoading) {
        return <Loading mt={'80px'} />;
    }

    if (!meetingsDoc || !meetingsToSave) {
        return <FailedToGetData />;
    }

    return (
        <>
        {showSettings &&
            <CleaningSettings
                closeModal={() => setShowSettings(false)}
            />
        }
        <H2 title={'LIMPIEZA DEL SALÓN'} />
        {!showCrud ?
            <>
            <RadioSelector
                options={[
                    { label: radioValue === '1' ? 'Viendo Próximos' : 'Ver Próximas', type: '1' },
                    { label: radioValue === '2' ? 'Viendo Todas' : 'Ver Todas', type: '2' }
                ]}
                radioValue={radioValue}
                setRadioValue={setRadioValue}
            />
            <div className={'d-flex justify-content-center gap-2 my-4'}>
                <button className={'btn btn-general-red'} onClick={() => setShowCrud(true)} disabled={!config.numberOfGroups}>
                    <BsPencil className={'mb-1 me-2'} />Editar Asignaciones
                </button>
                {!config.numberOfGroups &&
                    <p className={'ms-3 mt-1 mb-0'}>
                        *No están creados los grupos de predicación
                    </p>
                }
                <button className={'btn btn-general-red'} style={{ width: '200px' }} onClick={() => setShowSettings(true)}>
                    <FaExternalLinkAlt className={'mb-1 me-2'} />Ver Opciones
                </button>
            </div>
            </>
            :
            <div className={'d-flex justify-content-center my-4'}>
                <button className={'btn btn-general-red'}
                    onClick={() => {
                        setMeetingsToSave(x => [{
                            attendance: newAttendance,
                            avAssignments: newAvAssignments,
                            date: '',
                            groupsForCleaning: [],
                            id: 0,
                            sections: [],
                            type: null,
                            usherAssignments: newUsherAssignments,
                            weekday: 0,
                            weekNumber: 0,
                            year: 0
                        }, ...x!]);
                    }}
                >
                    <BsPlusCircle className={'mb-1 me-2'} />Agregar Reunión
                </button>
            </div>
        }
        <RoomCleaningTable
            isEdit={showCrud}
            meetings={showCrud ? meetingsToSave : sample}
            setMeetingsToSave={setMeetingsToSave}
        />
        {showCrud &&
            <FloatingSaveBar
                cancelHandler={cancelHandler}
                isDisabledSaveButton={!mts.length && !meetingsToDelete.length}
                saveHandler={saveHandler}
            />
        }
        </>
    );
}
